/**
 * EvidenceTrackSidebar
 * Left sidebar showing evidence tracks with status icons, progress count,
 * and a "Start Analysis" button.
 */

import {
  FileText,
  Code2,
  Cloud,
  CheckCircle2,
  SkipForward,
  Circle,
  LayoutDashboard,
  ChevronRight,
} from "lucide-react";
import { EvidenceTrackStatus } from "@app/types";
import type { EvidenceTrackStatuses, TrackStatusInfo } from "@app/types";
import type { ActiveTrack } from "@/layouts/EvidenceCollectionLayout";
import { useStartAnalysis } from "@/hooks/useEvidenceTracks";

interface EvidenceTrackSidebarProps {
  trackStatuses: EvidenceTrackStatuses | undefined;
  activeTrack: ActiveTrack;
  onTrackSelect: (track: ActiveTrack) => void;
  assessmentId: string;
  onRefetch: () => void;
  /**
   * Optional sub-item label shown indented beneath the active track.
   * Used by EvidenceFindingsWrapper to indicate "Findings" is the current page
   * without requiring a new sidebar mode.
   */
  activeSubItem?: string;
}

const trackIcons: Record<string, typeof FileText> = {
  documents: FileText,
  codeScan: Code2,
  infrastructure: Cloud,
};

function StatusIcon({ status }: { status: EvidenceTrackStatus }) {
  switch (status) {
    case EvidenceTrackStatus.COMPLETE:
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case EvidenceTrackStatus.IN_PROGRESS:
      return (
        <span className="relative flex h-4 w-4">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex h-4 w-4 rounded-full bg-blue-500" />
        </span>
      );
    case EvidenceTrackStatus.SKIPPED:
      return <SkipForward className="h-4 w-4 text-muted-foreground" />;
    case EvidenceTrackStatus.COMING_SOON:
      return (
        <Circle className="h-4 w-4 text-muted-foreground/50 [stroke-dasharray:3_3]" />
      );
    default:
      return <Circle className="h-4 w-4 text-muted-foreground" />;
  }
}

function TrackButton({
  track,
  trackKey,
  isActive,
  onClick,
}: {
  track: TrackStatusInfo;
  trackKey: ActiveTrack;
  isActive: boolean;
  onClick: () => void;
}) {
  const Icon = trackIcons[track.id] || Circle;
  const isDisabled = track.status === EvidenceTrackStatus.COMING_SOON;

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors
        ${isActive ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-muted"}
        ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1 truncate">{track.label}</span>
      <StatusIcon status={track.status} />
    </button>
  );
}

/**
 * Left sidebar showing evidence tracks with status icons, completion progress,
 * and a Start Analysis control. Used inside the evidence-collection layout and
 * (via EvidenceFindingsWrapper) inside evidence-phase findings routes.
 *
 * @param props - Component props
 * @param props.trackStatuses - Current status keyed by track for status badges and progress count
 * @param props.activeTrack - Track key currently highlighted in the sidebar
 * @param props.onTrackSelect - Callback fired when the user clicks a track button
 * @param props.assessmentId - Assessment ID used by the embedded Start Analysis control
 * @param props.onRefetch - Callback to refresh assessment data after Start Analysis succeeds
 * @param props.activeSubItem - Optional sub-item label rendered beneath the active track (e.g. "Findings")
 * @returns Sidebar element containing track buttons, progress, and analysis controls
 */
export function EvidenceTrackSidebar({
  trackStatuses,
  activeTrack,
  onTrackSelect,
  assessmentId,
  onRefetch,
  activeSubItem,
}: EvidenceTrackSidebarProps): JSX.Element {
  const { startAnalysis, isStarting } = useStartAnalysis(
    assessmentId,
    onRefetch,
  );

  const canStartAnalysis = trackStatuses?.canStartAnalysis ?? false;
  const resolvedCount = trackStatuses?.resolvedCount ?? 0;
  const totalCount = trackStatuses?.totalCount ?? 0;

  const tracks: { key: ActiveTrack; info: TrackStatusInfo | undefined }[] = [
    { key: "documents", info: trackStatuses?.documents },
    { key: "codeScan", info: trackStatuses?.codeScan },
    { key: "infrastructure", info: trackStatuses?.infrastructure },
  ];

  return (
    <aside
      data-testid="evidence-track-sidebar"
      className="flex flex-col w-64 border-r border-border bg-muted/30 shrink-0 h-full overflow-hidden"
    >
      {/* Overview button */}
      <div className="p-3 border-b border-border">
        <button
          onClick={() => onTrackSelect("overview")}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors
            ${activeTrack === "overview" ? "bg-primary/10 text-primary font-medium" : "text-foreground hover:bg-muted"}
          `}
        >
          <LayoutDashboard className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">Overview</span>
        </button>
      </div>

      {/* Evidence tracks */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
          Evidence Tracks
        </p>
        {tracks.map(({ key, info }) =>
          info ? (
            <div key={key}>
              <TrackButton
                track={info}
                trackKey={key}
                isActive={activeTrack === key}
                onClick={() => {
                  if (info.status !== EvidenceTrackStatus.COMING_SOON) {
                    onTrackSelect(key);
                  }
                }}
              />
              {activeTrack === key && activeSubItem && (
                <div
                  data-testid={`evidence-sidebar-sub-item-${key}`}
                  className="ml-6 mt-0.5 flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium text-primary bg-primary/5"
                >
                  <ChevronRight className="h-3 w-3 flex-shrink-0" />
                  {activeSubItem}
                </div>
              )}
            </div>
          ) : null,
        )}
      </div>

      {/* Progress + Start Analysis */}
      <div className="p-3 border-t border-border space-y-3">
        <p className="text-xs text-muted-foreground text-center">
          {resolvedCount} of {totalCount} tracks complete
        </p>
        <button
          data-testid="start-analysis-button"
          onClick={() => startAnalysis()}
          disabled={!canStartAnalysis || isStarting}
          className="w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors
            bg-primary text-primary-foreground hover:bg-primary/90
            disabled:opacity-50 disabled:cursor-not-allowed
            flex items-center justify-center gap-2"
        >
          {isStarting ? "Starting..." : "Start Analysis"}
          {!isStarting && <ChevronRight className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );
}
